import { useEffect, useMemo, useState } from 'react'
import AppLayout from '../../components/layout/AppLayout'
import { useSeparation } from '../../hooks/useSeparation'
import { fetchFilialNeeds, type FilialNeedRow } from '../../lib/filialNeeds'
import { generateSeparationPdfBlob } from '../../lib/separationPdf'
import { PRIORITY_BADGE } from '../../lib/replenishment'
import { separationStatusColor as STATUS_BADGE } from '../../lib/statusStyles'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../hooks/useToast'
import type { Company, ReplenishmentPriority, SeparationListWithItems, SeparationStatus } from '../../types'

// Itens que entram na lista: azul "Comprar" + Baixo + Zerado (decisão do projeto).
const SEPARATION_PRIORITIES: ReplenishmentPriority[] = ['Comprar', 'Baixo', 'Zerado']

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export default function SeparationPage() {
  const { filiais, isMatriz, lists, loading, createList, updateStatus, deleteList } = useSeparation()
  const { notify } = useToast()
  const [company, setCompany] = useState<Company | null>(null)
  // Saldo atual da matriz (BMS) por SKU — apoia a decisão de separar ou não.
  const [matrizStock, setMatrizStock] = useState<Map<string, number>>(new Map())

  // Estado da criação (matriz)
  const [filialId, setFilialId] = useState('')
  const [needs, setNeeds] = useState<FilialNeedRow[]>([])
  const [needsLoading, setNeedsLoading] = useState(false)
  const [overrides, setOverrides] = useState<Record<string, { selected: boolean; qty: number }>>({})
  const [notes, setNotes] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      supabase.from('profiles').select('company_id').eq('id', user.id).single().then(({ data: profile }) => {
        if (!profile) return
        supabase.from('companies').select('*').eq('id', profile.company_id).single().then(({ data }) => {
          if (data) setCompany(data as Company)
        })
      })
    })
    // Saldo da matriz por SKU (RLS já filtra pela empresa ativa = matriz).
    supabase.from('products').select('sku, current_stock').is('deleted_at', null).then(({ data }) => {
      if (!data) return
      setMatrizStock(new Map(data.map(p => [p.sku as string, Number(p.current_stock)])))
    })
  }, [])

  // Carrega a necessidade quando uma filial é escolhida.
  useEffect(() => {
    if (!filialId) { setNeeds([]); setOverrides({}); return }
    setNeedsLoading(true)
    fetchFilialNeeds()
      .then(rows => {
        const relevant = rows.filter(r =>
          r.filial_company_id === filialId &&
          r.purchase_suggestion > 0 &&
          SEPARATION_PRIORITIES.includes(r.priority)
        )
        setNeeds(relevant)
        // Pré-seleciona tudo com a sugestão como quantidade.
        const ov: Record<string, { selected: boolean; qty: number }> = {}
        relevant.forEach(r => { ov[r.sku] = { selected: true, qty: r.purchase_suggestion } })
        setOverrides(ov)
      })
      .catch(e => notify('error', `Erro ao carregar necessidade: ${e instanceof Error ? e.message : 'tente novamente.'}`))
      .finally(() => setNeedsLoading(false))
  }, [filialId, notify])

  const selectedItems = useMemo(
    () => needs.filter(r => overrides[r.sku]?.selected && (overrides[r.sku]?.qty ?? 0) > 0),
    [needs, overrides]
  )

  function toggle(sku: string) {
    setOverrides(prev => ({ ...prev, [sku]: { selected: !prev[sku]?.selected, qty: prev[sku]?.qty ?? 0 } }))
  }
  function setQty(sku: string, qty: number) {
    setOverrides(prev => ({ ...prev, [sku]: { selected: prev[sku]?.selected ?? true, qty } }))
  }
  function toggleAll(select: boolean) {
    setOverrides(prev => {
      const next = { ...prev }
      needs.forEach(r => { next[r.sku] = { selected: select, qty: next[r.sku]?.qty ?? r.purchase_suggestion } })
      return next
    })
  }

  const filialName = filiais.find(f => f.filial_company_id === filialId)?.filial_name ?? '—'

  async function handleCreate() {
    if (!filialId) { notify('error', 'Selecione a filial.'); return }
    if (selectedItems.length === 0) { notify('error', 'Selecione ao menos um item.'); return }
    setCreating(true)
    try {
      const items = selectedItems.map(r => ({
        sku: r.sku,
        product_name: r.product_name,
        quantity: overrides[r.sku].qty,
        priority: r.priority,
        current_stock: r.current_stock,
        matriz_stock: matrizStock.get(r.sku) ?? null,
      }))
      const res = await createList({ filialCompanyId: filialId, items, notes: notes.trim() || null })
      if ('error' in res) { notify('error', res.error); return }
      // Gera o PDF da lista recém-criada.
      if (company) {
        const blob = await generateSeparationPdfBlob({ matriz: company, filialName, items, notes: notes.trim() || null })
        downloadBlob(blob, `separacao-${filialName}-${new Date().toISOString().slice(0, 10)}.pdf`)
      }
      notify('success', 'Lista de separação criada.')
      setFilialId('')
      setNotes('')
    } finally {
      setCreating(false)
    }
  }

  async function downloadListPdf(list: SeparationListWithItems) {
    if (!company) { notify('error', 'Empresa não carregada, recarregue a página.'); return }
    const fName = filiais.find(f => f.filial_company_id === list.filial_company_id)?.filial_name ?? 'Filial'
    const blob = await generateSeparationPdfBlob({
      matriz: company,
      filialName: fName,
      items: list.separation_list_items,
      createdAt: list.created_at,
      notes: list.notes,
    })
    downloadBlob(blob, `separacao-${fName}-${fmtDate(list.created_at)}.pdf`)
  }

  async function changeStatus(listId: string, status: SeparationStatus) {
    const err = await updateStatus(listId, status)
    if (err) notify('error', err)
    else notify('success', `Status atualizado para "${status}".`)
  }

  async function handleDelete(list: SeparationListWithItems) {
    if (!confirm(`Excluir esta lista de separação de ${fmtDate(list.created_at)} (${list.separation_list_items?.length ?? 0} itens)? Esta ação é irreversível.`)) return
    const err = await deleteList(list.id)
    if (err) notify('error', err)
    else notify('success', 'Lista excluída.')
  }

  return (
    <AppLayout title="Abastecimento Filial">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-base-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Criação (apenas matriz com filiais vinculadas) */}
          {isMatriz && (
            <div className="bg-surface border border-line rounded-xl p-4 space-y-4">
              <h2 className="text-ink font-semibold">Nova lista de separação</h2>
              <div className="flex flex-wrap gap-3 items-center">
                <select
                  value={filialId}
                  onChange={e => setFilialId(e.target.value)}
                  className="bg-paper border border-line rounded px-3 py-1.5 text-ink text-sm min-w-[220px]"
                >
                  <option value="">Selecione a filial...</option>
                  {filiais.map(f => (
                    <option key={f.filial_company_id} value={f.filial_company_id}>{f.filial_name}</option>
                  ))}
                </select>
                {needs.length > 0 && (
                  <>
                    <button onClick={() => toggleAll(true)} className="text-xs px-3 py-1.5 bg-sunken hover:bg-line text-ink-muted rounded border border-line">Selecionar todos</button>
                    <button onClick={() => toggleAll(false)} className="text-xs px-3 py-1.5 bg-sunken hover:bg-line text-ink-muted rounded border border-line">Limpar</button>
                  </>
                )}
              </div>

              {needsLoading ? (
                <p className="text-ink-faint text-sm">Carregando necessidade da filial...</p>
              ) : filialId && needs.length === 0 ? (
                <p className="text-ink-faint text-sm">Nenhum item da filial precisa de reposição no momento.</p>
              ) : needs.length > 0 && (
                <>
                  <div className="overflow-auto max-h-[50vh] border border-line rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-sunken text-ink-muted">
                        <tr>
                          <th className="px-3 py-2 text-left w-10"></th>
                          <th className="px-3 py-2 text-left">SKU</th>
                          <th className="px-3 py-2 text-left">Produto</th>
                          <th className="px-3 py-2 text-right">Estoque Filial</th>
                          <th className="px-3 py-2 text-right">Estoque BMS</th>
                          <th className="px-3 py-2 text-left">Prioridade</th>
                          <th className="px-3 py-2 text-right">Sugestão</th>
                          <th className="px-3 py-2 text-right">Qtd a separar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {needs.map(r => (
                          <tr key={r.sku} className="border-b border-line text-ink-muted">
                            <td className="px-3 py-2">
                              <input type="checkbox" checked={overrides[r.sku]?.selected ?? false} onChange={() => toggle(r.sku)} />
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{r.sku}</td>
                            <td className="px-3 py-2 max-w-xs truncate" title={r.product_name}>{r.product_name}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.current_stock}</td>
                            {(() => {
                              const bms = matrizStock.get(r.sku)
                              if (bms === undefined) {
                                return <td className="px-3 py-2 text-right tabular-nums text-ink-faint" title="A BMS não possui este SKU">—</td>
                              }
                              const qty = overrides[r.sku]?.qty ?? 0
                              const insufficient = bms <= 0 || bms < qty
                              return (
                                <td
                                  className={`px-3 py-2 text-right tabular-nums font-medium ${insufficient ? 'text-red-400' : ''}`}
                                  title={insufficient ? 'Saldo da BMS insuficiente para separar' : undefined}
                                >
                                  {bms}
                                </td>
                              )
                            })()}
                            <td className="px-3 py-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${PRIORITY_BADGE[r.priority]}`}>{r.priority}</span>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.purchase_suggestion}</td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number" min={0}
                                value={overrides[r.sku]?.qty ?? 0}
                                onChange={e => setQty(r.sku, Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                                className="w-20 bg-paper border border-line rounded px-2 py-1 text-right tabular-nums text-sm text-ink"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Observações (opcional)"
                    className="w-full bg-paper border border-line rounded px-3 py-2 text-ink text-sm"
                    rows={2}
                  />
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleCreate}
                      disabled={creating || selectedItems.length === 0}
                      className="px-4 py-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
                    >
                      {creating ? 'Gerando...' : `Gerar lista (${selectedItems.length} itens)`}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Histórico (matriz e filial) */}
          <div className="bg-surface border border-line rounded-xl">
            <div className="px-4 py-3 border-b border-line">
              <h2 className="text-ink font-semibold">Listas de separação</h2>
            </div>
            {lists.length === 0 ? (
              <p className="px-4 py-6 text-ink-faint text-sm">Nenhuma lista de separação ainda.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-sunken text-ink-muted">
                  <tr>
                    <th className="px-4 py-2 text-left">Data</th>
                    <th className="px-4 py-2 text-left">Itens</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {lists.map(l => (
                    <tr key={l.id} className="border-b border-line text-ink-muted">
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                      <td className="px-4 py-2">{l.separation_list_items?.length ?? 0}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_BADGE[l.status]}`}>{l.status}</span>
                      </td>
                      <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                        <button onClick={() => downloadListPdf(l)} className="text-xs px-2.5 py-1 bg-sunken hover:bg-line text-ink-muted rounded border border-line">PDF</button>
                        {isMatriz && l.status === 'criada' && (
                          <button onClick={() => changeStatus(l.id, 'separada')} className="text-xs px-2.5 py-1 bg-yellow-600/80 hover:bg-yellow-600 text-white rounded">Marcar separada</button>
                        )}
                        {l.status !== 'recebida' && (
                          <button onClick={() => changeStatus(l.id, 'recebida')} className="text-xs px-2.5 py-1 bg-green-700/80 hover:bg-green-700 text-white rounded">Marcar recebida</button>
                        )}
                        {isMatriz && (
                          <button onClick={() => handleDelete(l)} className="text-xs px-2.5 py-1 bg-danger/10 hover:bg-danger/20 text-danger rounded border border-danger/30">Excluir</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  )
}
